import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  type NotificationItem,
} from "../services/api";
import { useAuth } from "../services/auth-context";

const EVENT_LABELS: Record<string, string> = {
  inbound_response: "Risposta ricevuta",
  outbound_sent: "Sollecito inviato",
  ai_analysis_completed: "Analisi AI",
  delivery_failure: "Consegna fallita",
  invoice_status_changed: "Stato fattura",
  policy_escalation: "Escalation",
  import_completed: "Import completato",
};

const EVENT_COLORS: Record<string, string> = {
  inbound_response: "#0a9c8f",
  outbound_sent: "#1396d2",
  ai_analysis_completed: "#8b5cf6",
  delivery_failure: "#ef4444",
  policy_escalation: "#f59e0b",
  import_completed: "#6b7280",
  invoice_status_changed: "#6b7280",
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCountChange: (count: number) => void;
}

export default function NotificationCenter({ isOpen, onClose, onCountChange }: Props) {
  const { tokens } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !tokens?.access_token) return;
    setLoading(true);
    listNotifications(tokens.access_token, { status: "unread", limit: 20 })
      .then((data) => {
        setItems(data.items);
        onCountChange(data.unread_count);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isOpen, tokens?.access_token]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleClick = async (n: NotificationItem) => {
    if (tokens?.access_token && n.status === "unread") {
      await markNotificationRead(tokens.access_token, n.id);
      setItems((prev) => prev.filter((i) => i.id !== n.id));
      onCountChange(Math.max(0, items.length - 1));
    }
    if (n.link) navigate(n.link);
    onClose();
  };

  const handleReadAll = async () => {
    if (!tokens?.access_token) return;
    await markAllNotificationsRead(tokens.access_token);
    setItems([]);
    onCountChange(0);
  };

  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        top: "48px",
        right: "12px",
        width: "380px",
        maxHeight: "480px",
        background: "#fff",
        border: "1px solid #d8e6ee",
        borderRadius: "12px",
        boxShadow: "0 8px 32px rgba(0,0,0,.12)",
        zIndex: 1000,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "1px solid #e4edf3",
        }}
      >
        <strong style={{ fontSize: "14px", color: "#1a2b3c" }}>Notifiche</strong>
        {items.length > 0 && (
          <button
            onClick={handleReadAll}
            style={{
              background: "none",
              border: "none",
              color: "#0a9c8f",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            Segna tutte come lette
          </button>
        )}
      </div>

      <div style={{ overflowY: "auto", flex: 1 }}>
        {loading && (
          <div style={{ padding: "24px", textAlign: "center", color: "#6d8494", fontSize: "13px" }}>
            Caricamento...
          </div>
        )}
        {!loading && items.length === 0 && (
          <div style={{ padding: "24px", textAlign: "center", color: "#6d8494", fontSize: "13px" }}>
            Nessuna notifica non letta
          </div>
        )}
        {items.map((n) => (
          <div
            key={n.id}
            onClick={() => handleClick(n)}
            className="notification-item"
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid #f0f4f8",
              cursor: n.link ? "pointer" : "default",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
              <span
                style={{
                  display: "inline-block",
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: EVENT_COLORS[n.event_type] || "#6b7280",
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: "11px", color: "#6d8494", textTransform: "uppercase", letterSpacing: ".3px" }}>
                {EVENT_LABELS[n.event_type] || n.event_type}
              </span>
              {n.channel_origin && (
                <span style={{ fontSize: "10px", color: "#9bb0bf", marginLeft: "auto" }}>
                  {n.channel_origin}
                </span>
              )}
            </div>
            <div style={{ fontSize: "13px", fontWeight: 600, color: "#1a2b3c", marginBottom: "2px" }}>
              {n.title}
            </div>
            <div style={{ fontSize: "12px", color: "#4d6575", lineHeight: "1.4" }}>{n.body}</div>
            <div style={{ fontSize: "10px", color: "#9bb0bf", marginTop: "4px" }}>
              {new Date(n.created_at).toLocaleString("it-IT")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
